import {
  LessonWithCard,
  PlayingState,
  PlaylistInfoState,
} from "app/playlists/types"
import { useAtomValue, useSetAtom } from "jotai"
import { useAtomCallback } from "jotai/utils"
import { MutableRefObject, useCallback } from "react"
import {
  audioShuffleAtom,
  currentPlayingItemAtom,
  isPlayingAtom,
  nextTrackAtom,
  playingStateAtom,
  playlistAtom,
  playlistInfoAtom,
  previousTrackAtom,
} from "./state"
import shuffleArray from "utils/global/shuffleArray"
import getPlaylistInfo from "app/playlists/utils/getPlaylistInfo"
import { GetUserListResult } from "app/playlists/queries/getUserList"
import getUserPlaylistTitle from "app/playlists/utils/getUserPlaylistTitle"
import { GetThemeGroupResult } from "app/themes/queries/getThemeGroup"
import { GetThemeResult } from "app/themes/queries/getTheme"
import { DisplayStateType } from "./types"

export default function useRadio({
  getUrl,
  pause,
  play,
  ref,
}: {
  getUrl: (lesson: LessonWithCard) => string | null
  pause: () => void
  play: () => void
  ref: HTMLAudioElement | null
}) {
  // const playingState = useAtomValue(playingStateAtom)

  /**
   * setList is a function that updates the current playlist in the radio player.
   *
   * - It takes an optional array of lessons (each with a LessonWithCard object).
   * - It filters out any lessons that do not have a playable audio or video file (using getUrl).
   * - It sets the filtered list as the current playlist (playlistAtom).
   * - It checks the index of the currently playing lesson in the new playlist.
   *   - If no lesson is currently playing (index === -1), it sets the playing state to the first lesson in the new list (or null if the list is empty), and pauses playback.
   *
   * This ensures that the playlist only contains playable lessons, and that the player is reset if the current lesson is not found in the new playlist.
   */
  const setList = useAtomCallback(
    useCallback(
      (
        get,
        set,
        lessons?: { lesson: LessonWithCard }[] | null,
        playlistInfo: PlaylistInfoState | null = null,
        item: { lesson: LessonWithCard } | null = null
      ) => {
        const list = lessons?.filter(({ lesson }) => !!getUrl(lesson)) || null
        if (list && list.length > 0) {
          const index = item ? list.findIndex((lesson) => lesson === item) : 0
          set(playingStateAtom, {
            type: "VISIBLE" as const,
            index,
            list,
            displayState: DisplayStateType["SMALL"],
            playlistInfo,
            isMuted: false,
            isPlaying: false,
            isLoading: false,
          })
          // pause()
        }
      },
      [getUrl]
    )
  )

  const toggleShuffle = useAtomCallback(
    useCallback((get, set) => {
      const playingState = get(playingStateAtom)
      const shuffleActive = get(audioShuffleAtom)
      const currentPlaylist = get(playlistAtom)

      if (!currentPlaylist || playingState.type === "NONE") return

      const currentPlayingItem = get(currentPlayingItemAtom)

      if (shuffleActive) {
        const sortedArray = [...currentPlaylist].sort(
          (
            a: { lesson: LessonWithCard; sort: number },
            b: { lesson: LessonWithCard; sort: number }
          ) => b.sort - a.sort
        )
        set(audioShuffleAtom, false)
        set(playingStateAtom, {
          ...playingState,
          list: sortedArray,
          index:
            sortedArray.findIndex(
              (item) => item.lesson?.id === currentPlayingItem?.lesson?.id
            ) || 0,
        })
        return
      } else {
        const currentPlayingItem = get(currentPlayingItemAtom)
        if (!currentPlayingItem) return
        const shuffledArray = shuffleArray([...currentPlaylist])
        if (!shuffledArray || !playingState) return -1

        // Get index for current song in newly shuffled playlist
        const index = shuffledArray.findIndex(
          (lesson) => lesson.lesson?.id === currentPlayingItem.lesson?.id
        )

        if (index !== 0) {
          // Swapping song at index ${index} with index 0
          ;[shuffledArray[index], shuffledArray[0]] = [
            shuffledArray[0],
            shuffledArray[index],
          ]
        }
        set(audioShuffleAtom, true)
        set(playlistAtom, shuffledArray)
        return
      }
    }, [])
  )

  const set = useAtomCallback(
    useCallback(
      (
        get,
        set,
        state: PlayingState,
        lessons?: { lesson: LessonWithCard }[] | null
      ) => {
        if (!state) {
          set(playingStateAtom, state)
          pause()
          return
        }
        if (!ref) return
        const url =
          state.type === "VISIBLE"
            ? getUrl(state.list[state.index].lesson)
            : null
        if (!url) return

        ref.src = url
        set(playingStateAtom, state)
        if (lessons) {
          set(
            playlistAtom,
            lessons.filter(({ lesson }) => !!getUrl(lesson))
          )
        }
        play()
      },
      [getUrl, pause, play, ref]
    )
  )

  const onFinish = useAtomCallback((get) => {
    const nextTrack = get(nextTrackAtom)
    const currentState = get(playingStateAtom)
    if (!currentState || currentState.type !== "VISIBLE") return
    if (nextTrack) {
      set({
        ...currentState,
        type: "VISIBLE",
        index: nextTrack,
      })
    }
  })

  const playLesson = useAtomCallback(
    useCallback(
      async (get, set, lesson: LessonWithCard) => {
        const playingState = get(playingStateAtom)
        if (playingState.type === "NONE") {
          await set(playingStateAtom, {
            type: "VISIBLE",
            index: 0,
            list: [{ lesson }],
            displayState: DisplayStateType["SMALL"],
            playlistInfo: null,
            isMuted: false,
            isPlaying: false,
            isLoading: false,
          })
        } else if (playingState.type === "VISIBLE") {
          await set(playingStateAtom, {
            ...playingState,
            index: 0,
            list: [{ lesson }],
            playlistInfo: null,
          })
        }
        await play()
      },
      [play]
    )
  )

  const playUserPlaylist = useAtomCallback(
    useCallback(
      async (
        get,
        set,
        userPlaylist: NonNullable<GetUserListResult>,
        item: { lesson: LessonWithCard } | null = null
      ) => {
        const playlistInfo = get(playlistInfoAtom)
        const isPlaying = get(isPlayingAtom)
        const playingState = get(playingStateAtom)

        if (
          !item &&
          isPlaying &&
          userPlaylist.id === playlistInfo?.libraryPlaylistId
        ) {
          await pause()
        } else if (
          !item &&
          !isPlaying &&
          userPlaylist.id === playlistInfo?.libraryPlaylistId
        ) {
          if (
            playingState.type === "VISIBLE" &&
            playingState.displayState === DisplayStateType["MINIMIZED"]
          )
            set(playingStateAtom, {
              ...playingState,
              displayState: DisplayStateType["SMALL"],
            })
          await play()
        } else {
          if (
            userPlaylist.type === "THEME" &&
            userPlaylist.themeGroup &&
            userPlaylist.themeGroup.theme
          ) {
            const categorySlug = userPlaylist.themeGroup.theme.category.slug
            const themeSlug = userPlaylist.themeGroup.theme.slug
            const groupSlug = userPlaylist.themeGroup.slug
            await setList(
              userPlaylist.themeGroup?.globalPlaylist?.playlist?.lessons,
              {
                listName: getUserPlaylistTitle(userPlaylist),
                route: {
                  href: `/categorieen/${categorySlug}/${themeSlug}/${groupSlug}`,
                },
                libraryPlaylistId: userPlaylist.id
                  ? userPlaylist.id
                  : undefined,
              },
              item
            )
          } else {
            console.log("setting playlist")
            // Set playlist in global player
            await setList(
              userPlaylist.playlist?.lessons,
              {
                listName: getUserPlaylistTitle(userPlaylist),
                libraryPlaylistId: userPlaylist.id
                  ? userPlaylist.id
                  : undefined,
              },
              item
            )
          }

          if (ref) ref.currentTime = 0

          await play()
          if (
            playingState.type === "VISIBLE" &&
            playingState.displayState === DisplayStateType["MINIMIZED"]
          ) {
            set(playingStateAtom, {
              ...playingState,
              displayState: DisplayStateType["SMALL"],
            })
          }
        }
      },
      [pause, play, ref, setList]
    )
  )

  const playThemeGroupPlaylist = useAtomCallback(
    useCallback(
      async (get, set, group: GetThemeGroupResult, theme: GetThemeResult) => {
        const isPlaying = get(isPlayingAtom)
        const isCurrentThemePlaying = get(currentPlayingItemAtom)
        const playingState = get(playingStateAtom)
        if (isPlaying && isCurrentThemePlaying) {
          await pause()
        } else {
          const { globalPlaylist, userLists } = group
          const playlist = globalPlaylist?.playlist
          const userListId = userLists?.[0]?.id
          if (!playlist) return
          // Set playlist in global player
          await setList(group.globalPlaylist?.playlist?.lessons, {
            listName: playlist.displayTitle,
            route: {
              href: `/categorieen/${theme.category.slug}/${theme.slug}/${group.slug}`,
            },
            libraryPlaylistId: userListId,
          })

          await play()
          if (
            playingState.type === "VISIBLE" &&
            playingState.displayState === DisplayStateType["MINIMIZED"]
          )
            set(playingStateAtom, {
              ...playingState,
              displayState: DisplayStateType["SMALL"],
            })
        }
      },
      [pause, play, setList]
    )
  )

  const playPreviousTrack = useAtomCallback(
    useCallback(
      (get, set) => {
        const playingState = get(playingStateAtom)
        if (playingState.type === "NONE") {
          return
        }
        const previousTrack = get(previousTrackAtom)
        if (previousTrack === null) return
        if (playingState.type === "VISIBLE") {
          set(playingStateAtom, {
            ...playingState,
            index: previousTrack,
          })
          // Wait for the audio to be ready before playing
          if (ref) {
            ref.src = getUrl(playingState.list[previousTrack].lesson) || ""
            play()
          }
        }
      },
      [ref, play, getUrl]
    )
  )

  const playNextTrack = useAtomCallback(
    useCallback(
      (get, set) => {
        const playingState = get(playingStateAtom)
        if (playingState.type === "NONE") {
          return
        }
        const nextTrack = get(nextTrackAtom)
        if (nextTrack === null) return
        set(playingStateAtom, {
          ...playingState,
          index: nextTrack,
        })
        // Wait for the audio to be ready before playing
        if (ref) {
          ref.src = getUrl(playingState.list[nextTrack].lesson) || ""
          play()
        }
      },
      [ref, play, getUrl]
    )
  )

  const hide = useAtomCallback(
    useCallback((get, set) => {
      set(playingStateAtom, { type: "NONE" })
    }, [])
  )

  const close = useAtomCallback(
    useCallback((get, set) => {
      const playingState = get(playingStateAtom)
      if (playingState.type === "NONE") return
      set(playingStateAtom, {
        ...playingState,
        displayState: DisplayStateType["MINIMIZED"],
      })
    }, [])
  )

  return {
    player: ref,
    set,
    setList,
    play,
    getUrl,
    pause,
    toggleShuffle,
    onFinish,
    playUserPlaylist,
    playPreviousTrack,
    playThemeGroupPlaylist,
    playLesson,
    playNextTrack,
    hide,
    close,
  }
}
